import { useEffect, useRef, useState } from 'react'
import { Modal } from 'shared'
import { CLOSE_BTN } from 'assets/images'
import ConfirmOkModal from 'features/Race/components/ConfirmOkModal'
import Loading from 'shared/Loading'
import { useViewFiSDK } from 'hooks'
import ViewFiAdModalStyled from './styled'

const VIEWFI_PARTNER_ID = process.env.REACT_APP_VIEWFI_PARTNER_ID ?? ''
const VIEWFI_PARTNER_NAME = process.env.REACT_APP_VIEWFI_PARTNER_NAME ?? 'MetaHorse'

export interface ViewFiAdModalProps {
  type: 'required' | 'optional'
  showCloseBtn?: boolean
  onClose: () => void
  onRewardPending?: () => void
  onRewardRejected?: () => void
  onDailyLimitReached?: () => void
  title?: string
}

function ViewFiAdModal({ type, showCloseBtn = true, onClose, onRewardPending, onRewardRejected, onDailyLimitReached, title }: ViewFiAdModalProps) {
  const [showConfirm, setShowConfirm] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showRewardError, setShowRewardError] = useState(false)
  const [showDailyLimitError, setShowDailyLimitError] = useState(false)
  const [showTimeoutError, setShowTimeoutError] = useState(false)
  const onRewardPendingRef = useRef(onRewardPending)
  const onCloseRef = useRef(onClose)
  const onRewardRejectedRef = useRef(onRewardRejected)
  const onDailyLimitReachedRef = useRef(onDailyLimitReached)

  useEffect(() => {
    onRewardPendingRef.current = onRewardPending
    onCloseRef.current = onClose
    onRewardRejectedRef.current = onRewardRejected
    onDailyLimitReachedRef.current = onDailyLimitReached
  }, [onRewardPending, onClose, onRewardRejected, onDailyLimitReached])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const { isProcessing, processingMessage } = useViewFiSDK({
    enabled: true,
    pollTimeout: 10000,
    onAdCompleted: () => {
      onRewardPendingRef.current?.()
    },
    onRewardPending: undefined,
    onAdError: () => {
      setShowTimeoutError(true)
    },
    onRewardRejected: () => {
      setShowRewardError(true)
      onRewardRejectedRef.current?.()
    },
    onDailyLimitReached: () => {
      setShowDailyLimitError(true)
      onDailyLimitReachedRef.current?.()
    },
    onScriptError: () => {
      onRewardPendingRef.current?.()
    },
  })

  const handleCloseClick = () => {
    if (type === 'required') {
      setShowConfirm(true)
    } else {
      onClose()
    }
  }

  const handleOverlayClick = () => {
    if (!showCloseBtn) return // Fully mandatory — no dismiss at all
    if (type === 'required') {
      setShowConfirm(true)
    } else {
      onClose()
    }
  }

  return (
    <>
      <Modal onOverlayClick={handleOverlayClick}>
        <ViewFiAdModalStyled>
          <div className="viewfi-ad-modal">
            {((type === 'optional') || (type === 'required' && showCloseBtn)) && (
              <button
                className="close-btn p-0 position-absolute"
                role="button"
                onClick={handleCloseClick}
              >
                <img src={CLOSE_BTN} alt='close' />
              </button>
            )}

            {title && (
              <div className='race-name-container'>
                <div className='race-name color-white font-bold text-uppercase'>{title}</div>
              </div>
            )}

            {/* Ad Container Wrapper */}
            <div style={{ flex: 1, width: '100%', marginTop: '16px', display: isProcessing ? 'none' : 'block' }}>
              <div
                id="w3ap-ad-container"
                data-partner-id={VIEWFI_PARTNER_ID}
                data-partner-name={VIEWFI_PARTNER_NAME}
                data-format="video"
                style={{ width: '100%', height: '100%' }}
              />
            </div>

            {/* Processing State Container */}
            {isProcessing && (
              <div style={{ flex: 1, width: '100%', position: 'relative', marginTop: '16px' }}>
                <Loading content={processingMessage} />
              </div>
            )}
          </div>
        </ViewFiAdModalStyled>
      </Modal>

      {showConfirm && (
        <ConfirmOkModal
          heading='Leave advertisement?'
          message={
            <div style={{ color: '#fff' }}>
              You need to complete the ad
              to continue this action.
            </div>
          }
          btnOk='Leave'
          btnCancel='Keep Watching'
          btnCancelType="default"
          btnOkType="btnCancel"
          onCloseButtonClick={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false)
            onClose()
          }}
          isContentCenter
          width={150}
        />
      )}

      {/* {showRewardError && (
        <ConfirmOkModal
          heading='Reward Error'
          message={
            <div style={{ color: '#fff' }}>
              <p>Failed to deliver reward.</p>
              <p style={{ fontSize: '14px', color: '#aaa' }}>Please try watching another ad.</p>
            </div>
          }
          onCloseButtonClick={() => setShowRewardError(false)}
          isModalWarning
          btnName='OK'
          isContentCenter
          width={160}
        />
      )} */}

      {showDailyLimitError && (
        <ConfirmOkModal
          heading='Daily Limit Reached'
          message={
            <div style={{ color: '#fff' }}>
              <p>You have reached your daily ad view limit.</p>
              <p style={{ fontSize: '14px', color: '#aaa' }}>Please come back tomorrow or upgrade your plan.</p>
            </div>
          }
          onCloseButtonClick={() => {
            setShowDailyLimitError(false)
            onCloseRef.current?.()
          }}
          isModalWarning
          btnName='OK'
          isContentCenter
          width={160}
        />
      )}

      {showTimeoutError && (
        <ConfirmOkModal
          heading='Ad Timeout'
          message={
            <div style={{ color: '#fff' }}>
              <p>The ad took too long to respond.</p>
              <p style={{ fontSize: '14px', color: '#aaa' }}>Please try again later.</p>
            </div>
          }
          onCloseButtonClick={() => {
            setShowTimeoutError(false)
            onRewardPendingRef.current?.()
          }}
          isModalWarning
          btnName='OK'
          isContentCenter
          width={160}
        />
      )}
    </>
  )
}

export default ViewFiAdModal
